import { axiosInstance } from "../../client";

export const listLinks = (params: { seasonId: number; supplierId: number; includeDeleted?: boolean }) =>
	axiosInstance.get("SeasonDocumentSuppliers", { params }).then(r => r.data);

export const createLink = (payload: { season_id: number; document_id: number; supplier_id: number }) =>
	axiosInstance.post("SeasonDocumentSuppliers", payload).then(r => r.data);

export const deleteLink = (id: number) =>
	axiosInstance.delete(`SeasonDocumentSuppliers/${id}`);

export const restoreLink = (id: number) =>
	axiosInstance.post(`SeasonDocumentSuppliers/${id}/restore`);

export const getSupplierLatestDocs = (id: number) =>
	axiosInstance.get(`SeasonDocumentSuppliers/${id}/documents/latest-active`).then(r => r.data);

export function uploadSdsFile(params) {
    const fd = new FormData();
    fd.append('sds_id', String(params.sdsId));
    
    if (params.centerId != null) {
        fd.append('center_id', String(params.centerId));
    }
    
    params.files.forEach((file, index) => {
        fd.append('files', file);
    });
    
    if (params.fileNames && params.fileNames.length > 0) {
        params.fileNames.forEach((fileName, index) => {
            fd.append('fileNames', fileName || '');
        });
    }
    
    return axiosInstance.post('season-document-supplier-files/upload', fd, {
        transformRequest: (data) => data,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    })
    .then(response => {
        return response;
    })
    .catch(error => {
        throw error;
    });
}

export const getDocumentsRemaining = (supplierId: number) =>
  axiosInstance
    .get(`season-document-supplier-files/GetDocumentsRemaining/${supplierId}`)
    .then(r => r.data as Array<{ seasonName: string }>);

export interface SeasonMatrixDto {
  season_id: number;
  season_name: string;
  documents: Array<{
    id: number;
    name: string;
    multiple: boolean;
    perPlant: boolean;
  }>;
  suppliers: Array<{
    id: number;
    name: string;
  }>;
  cells: Array<{
    sds_id: number;
    document_id: number;
    supplier_id: number;
    status: number;
    files: Array<{
      id: number;
      url: string;
      center_id?: number;
      file_name: string;
    }>;
  }>;
}

export const getSeasonMatrixWithFilters = async (
  seasonId: number,
  params?: {
    supplierIds?: number[];
    documentIds?: number[];
    search?: string;
  }
) => {
  try {
    const urlParams = new URLSearchParams();
    urlParams.append('seasonId', seasonId.toString());
    
    if (params?.supplierIds?.length) {
      params.supplierIds.forEach(id => {
        urlParams.append('supplierIds', id.toString());
      });
    }
    
    if (params?.documentIds?.length) {
      params.documentIds.forEach(id => {
        urlParams.append('documentIds', id.toString());
      });
    }
    
    if (params?.search) {
      urlParams.append('search', params.search);
    }

    const response = await axiosInstance.get(
      `SeasonDocumentSuppliers/matrix?${urlParams.toString()}`
    );
    
    return response.data as SeasonMatrixDto;
  } catch (error) {
    console.error('Error fetching season matrix with filters:', error);
    throw error;
  }
};

export async function getSeasonMatrix(seasonId: number) {
  const { data } = await axiosInstance.get(`SeasonDocumentSuppliers/matrix`, { params: { seasonId } });
  return data as {
    season_id: number;
    season_name: string;
    documents: { id: number; name: string; multiple: boolean; perPlant: boolean }[];
    suppliers: { id: number; name: string }[];
    cells: { sds_id: number; document_id: number; supplier_id: number; status: number; files: { id:number; url:string; area_id?:number }[] }[];
  };
}

export function bulkUpdateSdsStatus(ids: [], status: number) {
  return axiosInstance.post('SeasonDocumentSuppliers/bulk-status', { ids, status });
}

export function uploadExcelSuppliersPerSeason(seasonId: number, file: File) {
	const fd = new FormData();
	fd.append('file', file);

	return axiosInstance.post(
		`SeasonDocumentSuppliers/UploadExcelSuppliersPerSeason/${seasonId}`,
		fd,
		{
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			transformRequest: (data) => data,
		}
	);
}

export const getDocumentsBySupplierSeason = (supplierId, seasonId, params?: {
  documentIds?: number[];
  search?: string;
}) => {
  const urlParams = new URLSearchParams();
  
  if (params?.documentIds?.length) {
    params.documentIds.forEach(id => {
      urlParams.append('documentIds', id.toString());
    });
  }
  
  if (params?.search) {
    urlParams.append('search', params.search);
  }
  
  let url = `SeasonDocumentSuppliers/GetDocumentsBySuppSeason/${supplierId}/${seasonId}`;
  const queryString = urlParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }
  
  return axiosInstance.get(url);
};

export async function downloadSupplierFiles(seasonId: number, supplierId: number, fileIds?: number[], documentId?: number) {
  try {
    let url = `SeasonDocumentSuppliers/GetFilesPerSeasonSupplier/${seasonId}/${supplierId}`;
    
    const queryParams = new URLSearchParams();
    
    if (documentId) {
      queryParams.append('documentId', documentId.toString());
    }
    
    if (fileIds && fileIds.length > 0) {
      fileIds.forEach(id => {
        queryParams.append('FileIds', id.toString());
      });
    }
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }
    
    const response = await axiosInstance.get(
      url,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/zip, application/octet-stream'
        }
      }
    );
    
    const contentDisposition = response.headers['content-disposition'];
    let filename = `${supplierId}_${seasonId}_archivos.zip`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/zip' });
    const urlObject = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = urlObject;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(urlObject);
    
    return response;
  } catch (error: any) {
    let backendMessage = null;
    
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          backendMessage = errorData.message;
        } catch {
          backendMessage = text;
        }
      } catch {
      }
    }
    
    if (!backendMessage && error.response?.data?.message) {
      backendMessage = error.response.data.message;
    }
    
    if (!backendMessage && error.message && error.message !== "Request failed with status code 404") {
      backendMessage = error.message;
    }
    
    if (backendMessage) {
      const newError = new Error(backendMessage);
      (newError as any).status = error.response?.status || 500;
      (newError as any).originalError = error;
      throw newError;
    }
    
    throw error;
  }
}

export interface DeleteSuppliersFromSeasonDto {
  SupplierIds: number[];
}

export async function deleteSuppliersPerSeason(seasonId: number, supplierIds: number[]) {
  try {
    const dto: DeleteSuppliersFromSeasonDto = {
      SupplierIds: supplierIds
    };
    
    const response = await axiosInstance.put(
      `SeasonDocumentSuppliers/DeleteSupplierPerSeason/${seasonId}`,
      dto
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Error eliminando proveedores de la temporada:', error);
    throw error;
  }
}

export const getAllDocuments = async (params: {
  includeDeleted?: boolean;
  search?: string;
  seasonId?: number; 
  supplierId?: number;
} = {}) => {
  try {
    const response = await axiosInstance.get('Documents', {
      params: {
        includeDeleted: false,
        ...params
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

export const listFileNames = (sdsId: number) => {
  return axiosInstance.get(`season-document-supplier-files/ListFileNames`, {
    params: { 
      sds_id: sdsId 
    }
  }).then(r => {
    return r.data; 
  }).catch(error => {
    throw error;
  });
}
